import Box from '@mui/material/Box';
import React, { useCallback } from 'react';

import {
  LaneTokens,
  STAGE_BUTTON_SX,
  STAGE_COUNT_SX,
  STAGE_LABEL_SX,
} from './ServiceFlowRail.styles';
import { ActivityStage } from '../../../@types/service-activity.type';
import { COLORS } from '../../../configs/colors';
import { MagnitudeBar } from '../../atoms/MagnitudeBar';

export interface FlowStageProps {
  stage: ActivityStage;
  tokens: LaneTokens;
  /** When false the stage renders as static text — no button, no focus stop. */
  canDrillDown: boolean;
  onSelect: (statusGroup: string) => void;
}

export const FlowStage: React.FC<FlowStageProps> = ({ stage, tokens, canDrillDown, onSelect }) => {
  const handleClick = useCallback(() => onSelect(stage.group), [onSelect, stage.group]);

  const body = (
    <>
      <Box component="span" sx={STAGE_LABEL_SX}>
        {stage.header}
      </Box>
      <Box
        component="span"
        sx={{
          ...STAGE_COUNT_SX,
          color: stage.count === 0 ? COLORS.ActivityZeroText : STAGE_COUNT_SX.color,
        }}
      >
        {stage.count.toLocaleString()}
      </Box>
      <MagnitudeBar
        value={stage.magnitude}
        fillColor={tokens.fill}
        trackColor={COLORS.ActivityRailTrack}
        testDataId={`flow-stage-bar-${stage.field}`}
      />
    </>
  );

  if (!canDrillDown) {
    return (
      <Box data-testid={`flow-stage-${stage.field}`} sx={{ ...STAGE_BUTTON_SX, cursor: 'default' }}>
        {body}
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      data-testid={`flow-stage-${stage.field}`}
      onClick={handleClick}
      aria-label={`${stage.header}: ${stage.count.toLocaleString()} jobs. Open the job list filtered to ${stage.header}.`}
      sx={{
        ...STAGE_BUTTON_SX,
        cursor: 'pointer',
        transition: 'background-color 140ms ease',
        '&:hover': { bgcolor: 'v4.tableHover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {body}
    </Box>
  );
};
